import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";

const PER_PAGE = 10;

export interface EditEmployee {
  nuid: string;
  privRole: string | null;
  firstName: string;
  lastName: string;
  middleName?: string;
  primaryRegion: string;
  kpAreaCd: string;
  kpSvcArea: string;
  glLocCd: string;
  entity: string;
  respAreaId: number;
  responsibilityRegion: string;
  responsibilityArea: string | number;
  responsibilityCenter: string | number;
  responsibilityLocation: string | number;
  responsibilityRegionDesc: string;
  responsibilityAreaDesc: string | null;
  responsibilityCenterDesc: string | null;
  responsibilityLocationDesc: string | null;
}

export interface UserAccess extends EditEmployee {
  description?: string;
  location?: string;
}

interface ManageAccessState {
  users: UserAccess[];
  searchResults: UserAccess[];
  searchLoading: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  currentPage: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  searchTotalItems: number;
  roleOptions: string[];
}

const initialState: ManageAccessState = {
  users: [],
  searchResults: [],
  searchLoading: false,
  loading: false,
  saving: false,
  error: null,
  currentPage: 1,
  perPage: PER_PAGE,
  totalItems: 0,
  totalPages: 0,
  searchTotalItems: 0,
  roleOptions: [],
};

// Shared helper to perform an API call and refresh the users list.
async function performAndRefresh(
  apiCall: () => Promise<any>,
  dispatch: any,
  page: number,
  rejectWithValue: (val: any) => any,
  fallbackMessage = "Request failed",
) {
  try {
    const response = await apiCall();

    // Check if response indicates failure via isSuccess field
    if (response?.data?.isSuccess === false) {
      const errorMessage = response.data.message ?? fallbackMessage;
      return rejectWithValue(errorMessage);
    }

    dispatch(fetchManageAccessUsers({ page }));
    return response.data;
  } catch (error: any) {
    if (
      error?.message === "canceled" ||
      error?.message === "Request cancelled" ||
      error?.code === "ECONNABORTED"
    ) {
      return rejectWithValue("Request cancelled");
    }
    const errorMessage =
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      error?.response?.data?.detail ??
      error?.response?.statusText ??
      error?.message ??
      fallbackMessage;
    return rejectWithValue(errorMessage);
  }
}

export const searchManageAccessUsers = createAsyncThunk(
  "manageAccess/searchManageAccessUsers",
  async (
    {
      region,
      fName,
      lName,
      searchKey,
      value,
      sortBy,
      sortOrder,
      page,
      perPage,
    }: {
      region: string;
      fName?: string;
      lName?: string;
      searchKey?: string;
      value?: string;
      sortBy?: string;
      sortOrder?: string;
      page?: number;
      perPage?: number;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = { signal };
      const url = endpoints.ubt.manageAccess.searchUsers({
        region,
        fName,
        lName,
        searchKey,
        value,
        sortBy,
        sortOrder,
        page,
        perPage,
      });
      const response = await api.get(url, options);
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.response?.data?.detail ??
        error?.response?.statusText ??
        error?.message ??
        "Failed to search users";
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchRoleOptions = createAsyncThunk(
  "manageAccess/fetchRoleOptions",
  async ({ nuid }: { nuid?: string }, { rejectWithValue, signal }) => {
    try {
      const options: any = { signal };
      // Use endpoint helper which optionally appends the nuid query param
      const response = await api.get(
        endpoints.ubt.manageAccess.getAccessActions(nuid),
        options,
      );
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.response?.data?.detail ??
        error?.response?.statusText ??
        error?.message ??
        "Failed to fetch role options";
      return rejectWithValue(errorMessage);
    }
  },
);
export const fetchManageAccessUsers = createAsyncThunk(
  "manageAccess/fetchManageAccessUsers",
  async ({ page }: { page: number }, { rejectWithValue, signal }) => {
    try {
      const options: any = {
        signal,
        params: {
          page,
          per_page: PER_PAGE,
        },
      };
      const response = await api.get(
        endpoints.ubt.manageAccess.getUsersList,
        options,
      );
      return response.data;
    } catch (error: any) {
      if (
        error?.message === "canceled" ||
        error?.message === "Request cancelled" ||
        error?.code === "ECONNABORTED"
      ) {
        return rejectWithValue("Request cancelled");
      }
      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        error?.response?.data?.detail ??
        error?.response?.statusText ??
        error?.message ??
        "Failed to fetch manage access users";
      return rejectWithValue(errorMessage);
    }
  },
);

export const deleteAccess = createAsyncThunk(
  "manageAccess/deleteAccess",
  async (
    { payload, page }: { payload: any; page: number },
    { dispatch, rejectWithValue, signal },
  ) => {
    const options: any = { signal };
    return performAndRefresh(
      () => api.patch(endpoints.ubt.manageAccess.deleteUser, payload, options),
      dispatch,
      page,
      rejectWithValue,
      "Failed to delete access",
    );
  },
);

export const addAccess = createAsyncThunk(
  "manageAccess/addAccess",
  async (
    { payload, page }: { payload: any; page: number },
    { dispatch, rejectWithValue, signal },
  ) => {
    const options: any = { signal };
    return performAndRefresh(
      () => api.post(endpoints.ubt.manageAccess.addUser, payload, options),
      dispatch,
      page,
      rejectWithValue,
      "Failed to add access",
    );
  },
);

export const editAccess = createAsyncThunk(
  "manageAccess/editAccess",
  async (
    { payload, page }: { payload: any; page: number },
    { dispatch, rejectWithValue, signal },
  ) => {
    const options: any = { signal };
    return performAndRefresh(
      () => api.put(endpoints.ubt.manageAccess.editAccess, payload, options),
      dispatch,
      page,
      rejectWithValue,
      "Failed to edit access",
    );
  },
);

const manageAccessSlice = createSlice({
  name: "manageAccess",
  initialState,
  reducers: {
    resetManageAccessState: (state) => {
      state.users = [];
      state.searchResults = [];
      state.searchLoading = false;
      state.loading = false;
      state.error = null;
      state.currentPage = 1;
      state.totalItems = 0;
      state.totalPages = 0;
    },
    setUsers(state, action: PayloadAction<UserAccess[]>) {
      state.users = action.payload;
    },
    addUser(state, action: PayloadAction<UserAccess>) {
      state.users.push(action.payload);
    },
    removeUser(state, action: PayloadAction<string>) {
      state.users = state.users.filter((u) => u.nuid !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchManageAccessUsers.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchManageAccessUsers.fulfilled, (state, action) => {
      state.loading = false;
      const payload: any = action.payload;
      if (payload?.meta && payload?.items && Array.isArray(payload.items)) {
        state.users = payload.items;
        state.totalItems = payload.meta.total ?? 0;
        state.totalPages = payload.meta.totalPages ?? 1;
        state.currentPage = payload.meta.pageNumber ?? 1;
        state.perPage = payload.meta.pageSize ?? 10;
      } else {
        state.users = payload ? [payload] : [];
      }
    });
    builder.addCase(fetchManageAccessUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
    builder.addCase(searchManageAccessUsers.pending, (state) => {
      state.searchLoading = true;
      state.error = null;
    });
    builder.addCase(searchManageAccessUsers.fulfilled, (state, action) => {
      state.searchLoading = false;
      const payload: any = action.payload;
      let items: any[];
      if (Array.isArray(payload)) {
        items = payload;
      } else if (Array.isArray(payload?.items)) {
        items = payload.items;
      } else if (payload) {
        items = [payload];
      } else {
        items = [];
      }
      state.searchResults = items;
      state.searchTotalItems =
        payload?.meta?.totalItems ??
        payload?.meta?.total ??
        payload?.totalItems ??
        payload?.total ??
        items.length;
    });
    builder.addCase(searchManageAccessUsers.rejected, (state, action) => {
      state.searchLoading = false;
      state.error = action.payload as string;
    });
    builder.addCase(fetchRoleOptions.fulfilled, (state, action) => {
      // Normalize backend response into an array of strings for roleOptions.
      const payload: any = action.payload;
      const toStrings = (arr: any[]) => {
        if (!Array.isArray(arr)) return [];
        if (arr.length === 0) return [];
        // array of primitives
        if (typeof arr[0] === "string") return arr as string[];
        // array of objects with displayText / id
        return arr
          .map((r: any) => r.displayText ?? r.id ?? String(r))
          .filter(Boolean);
      };

      if (Array.isArray(payload)) {
        state.roleOptions = toStrings(payload);
      } else if (payload && Array.isArray(payload.roles)) {
        state.roleOptions = toStrings(payload.roles);
      } else if (payload && Array.isArray(payload.items)) {
        state.roleOptions = toStrings(payload.items);
      } else {
        state.roleOptions = [];
      }
    });
    builder.addCase(fetchRoleOptions.rejected, (state, action) => {
      // On error, keep existing roleOptions and optionally set error
      state.error = action.payload as string;
    });
    builder.addCase(editAccess.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(editAccess.fulfilled, (state) => {
      state.saving = false;
    });
    builder.addCase(editAccess.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });
    builder.addCase(deleteAccess.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(deleteAccess.fulfilled, (state) => {
      state.saving = false;
    });
    builder.addCase(deleteAccess.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });
    builder.addCase(addAccess.pending, (state) => {
      state.saving = true;
      state.error = null;
    });
    builder.addCase(addAccess.fulfilled, (state) => {
      state.saving = false;
    });
    builder.addCase(addAccess.rejected, (state, action) => {
      state.saving = false;
      state.error = action.payload as string;
    });
  },
});

export const { resetManageAccessState, setUsers, addUser, removeUser } =
  manageAccessSlice.actions;
export default manageAccessSlice.reducer;
